import { useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function PasswordField({ id, label, name, value, onChange, show, onToggle, disabled }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          name={name}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="h-11 pr-10"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-11 w-11"
          onClick={onToggle}
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export default function CandidateAccountDialogs() {
  const { user } = useAuth();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwData, setPwData] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  if (!user || user.role !== 'candidate') return null;

  const handlePwChange = (e) => setPwData({ ...pwData, [e.target.name]: e.target.value });

  const handlePwSave = async () => {
    setPwLoading(true);
    setPwError('');
    setPwSuccess('');
    if (!pwData.currentPassword || !pwData.newPassword || !pwData.confirm) {
      setPwError('All fields are required.');
      setPwLoading(false);
      return;
    }
    if (pwData.newPassword !== pwData.confirm) {
      setPwError('New passwords do not match.');
      setPwLoading(false);
      return;
    }
    try {
      await apiClient.changePassword({
        currentPassword: pwData.currentPassword,
        newPassword: pwData.newPassword,
      });
      setPwSuccess('Password changed successfully.');
      setTimeout(() => setPwOpen(false), 1000);
    } catch (e) {
      setPwError(e.message || 'Failed to change password.');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <>
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Account details</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-muted-foreground">Name: </span>
              <span className="font-medium">
                {user.firstName} {user.lastName}
              </span>
            </p>
            <p>
              <span className="text-muted-foreground">Email: </span>
              <span className="font-medium">{user.email}</span>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Update your account password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {pwError && (
              <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {pwError}
              </div>
            )}
            {pwSuccess && (
              <div role="status" className="rounded-md border border-success/40 bg-success/10 px-4 py-3 text-sm text-success">
                {pwSuccess}
              </div>
            )}
            <PasswordField
              id="currentPassword"
              label="Current password"
              name="currentPassword"
              value={pwData.currentPassword}
              onChange={handlePwChange}
              show={showPasswords.current}
              onToggle={() => setShowPasswords((s) => ({ ...s, current: !s.current }))}
              disabled={pwLoading}
            />
            <PasswordField
              id="newPassword"
              label="New password"
              name="newPassword"
              value={pwData.newPassword}
              onChange={handlePwChange}
              show={showPasswords.new}
              onToggle={() => setShowPasswords((s) => ({ ...s, new: !s.new }))}
              disabled={pwLoading}
            />
            <PasswordField
              id="confirm"
              label="Confirm password"
              name="confirm"
              value={pwData.confirm}
              onChange={handlePwChange}
              show={showPasswords.confirm}
              onToggle={() => setShowPasswords((s) => ({ ...s, confirm: !s.confirm }))}
              disabled={pwLoading}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} disabled={pwLoading}>
              Cancel
            </Button>
            <Button onClick={handlePwSave} disabled={pwLoading}>
              {pwLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                'Change password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
